import { Link, Outlet, NavLink, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ROLES } from '@bcc/shared';
import { useAuth } from '../contexts/AuthContext';
import { useMaxWidth } from '../hooks/useMaxWidth';
import { FloatingCornerProvider } from '../contexts/FloatingCornerContext';
import { SiteFooter } from './SiteFooter';
import { MarketingCornerPopoverStack } from './MarketingCornerPopoverStack';
import { OpportunityCallout } from './OpportunityCallout';
import { UserProfileMenu } from './UserProfileMenu';

const pillNavCls = ({
  isActive,
}: {
  isActive: boolean;
}) =>
  `text-sm px-3.5 py-2 rounded-xl transition-all duration-200 ${
    isActive
      ? 'bg-[var(--header-shell-bg)]/20 font-semibold text-[var(--header-shell-bg)] shadow-sm shadow-black/[0.06]'
      : 'text-[var(--home-text-on-dark)]/90 hover:bg-white/[0.06] hover:text-[var(--home-miami-blue)]'
  }`;

const barNavCls = ({ isActive }: { isActive: boolean }) =>
  `text-[0.6rem] font-semibold uppercase tracking-[0.12em] transition-colors px-1.5 py-2 whitespace-nowrap sm:px-2 sm:text-[0.65rem] sm:tracking-[0.14em] ${
    isActive
      ? 'font-bold text-[var(--header-shell-bg)]'
      : 'text-[var(--home-text-on-dark)]/82 hover:text-[var(--home-miami-blue)]'
  }`;

function IconMenu(props: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

function IconClose(props: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden {...props}>
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
    </svg>
  );
}

const mobileNavLinkCls = ({ isActive }: { isActive: boolean }) =>
  `block rounded-xl px-4 py-3.5 text-base font-semibold tracking-wide transition-colors ${
    isActive
      ? 'bg-[var(--header-shell-bg)]/20 text-[var(--header-shell-bg)]'
      : 'text-[var(--home-text-on-dark)]/90 active:bg-white/[0.08]'
  }`;

const mobileNavCtaCls =
  'block rounded-xl bg-[var(--header-shell-bg)] px-4 py-3.5 text-center font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white shadow-md shadow-black/20 transition-colors active:brightness-110';

/** Shared shell — admins on /admin/* use vertical portal nav in AdminLayout; header shows profile only. */
export function AppShell() {
  const { session, loading, profile, role } = useAuth();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
/** Below this width we use the drawer + compact header (Tailwind `xl` = 1280px). */
  const isCompactHeader = useMaxWidth(1280);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const portalUser = (role === ROLES.ADMIN || role === ROLES.STAFF) && Boolean(session && profile);
  const onAdminRoute = location.pathname.startsWith('/admin');
  /** Logo + profile only (no duplicate portal links) — sidebar/staff pages cover navigation. */
  const compactPortalHeader =
    portalUser &&
    (onAdminRoute || location.pathname.startsWith('/staff') || location.pathname === '/account' || location.pathname === '/dashboard/account');
  const showAdminFooter = onAdminRoute;
  const onInvestorDashboard = location.pathname.startsWith('/dashboard');
  /** Signed-in users on the investor portal: no marketing links in the shell; mobile profile moves into the drawer. */
  const hideMarketingNav = Boolean(session && profile && onInvestorDashboard);
  const logoTo =
    !session || !profile
      ? '/'
      : onInvestorDashboard
        ? '/dashboard'
        : portalUser
          ? role === ROLES.ADMIN
            ? '/admin'
            : '/staff'
          : '/';
  /** Same nav shell width for investors, staff, and admins when signed in. */
  const signedInHeader = Boolean(session && profile);
  /** Match `InvestorLayout` / `AdminLayout` content rail (`max-w-[1400px]`), not `max-w-7xl`. */
  const portalContentWidthHeader =
    signedInHeader && (onInvestorDashboard || onAdminRoute);
  const pageTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const };

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    if (!isCompactHeader) closeMobileNav();
  }, [isCompactHeader, closeMobileNav]);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (!isCompactHeader || !mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isCompactHeader, mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileNav();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen, closeMobileNav]);

  return (
    <FloatingCornerProvider>
    <div className="relative isolate flex min-h-screen min-w-0 flex-col overflow-x-clip marketing-shell-bg">
      <motion.header
        initial={false}
        className="sticky top-3 z-[120] w-full px-2.5 sm:px-4 md:top-4 md:px-6 lg:top-5 lg:px-7"
      >
        <div
          className={`relative mx-auto w-full rounded-3xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.35)] ring-1 ring-white/[0.04] backdrop-blur-lg ${
            portalContentWidthHeader ? 'max-w-[1400px]' : signedInHeader ? 'max-w-7xl' : 'max-w-[100rem]'
          }`}
        >
          <div
            className={
              compactPortalHeader
                ? 'flex items-center justify-between gap-4 px-4 py-3'
                : 'relative flex items-center justify-between gap-4 px-4 py-3.5 md:px-6'
            }
          >
          <Link
            to={logoTo}
            className="relative z-20 flex min-w-0 shrink-0 items-start gap-2 font-display font-bold uppercase tracking-[0.08em] text-xs sm:text-sm md:items-center md:text-[0.95rem]"
          >
            <span
              className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[var(--header-shell-bg)] shadow-[0_0_12px_rgba(244_114_182/0.65)] md:mt-0"
              aria-hidden
            />
            <span className="flex min-w-0 flex-col leading-[1.1] md:flex-row md:items-baseline md:gap-2 md:leading-none">
              <span className="text-[var(--header-shell-bg)]">Breckenridge</span>
              <span className="font-normal text-[var(--home-miami-blue)]">Capital</span>
            </span>
          </Link>

          {compactPortalHeader ? (
            <div className="flex min-w-0 flex-1 items-center justify-end">{profile ? <UserProfileMenu /> : null}</div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              {!hideMarketingNav ? (
                <nav
                  className="hidden min-w-0 flex-1 justify-center overflow-hidden xl:flex"
                  aria-label="Primary"
                >
                  <div className="flex min-w-0 max-w-full items-center justify-center gap-x-1 overflow-x-auto sm:gap-x-1.5 2xl:gap-x-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <NavLink to="/" className={({ isActive }) => barNavCls({ isActive })} end>
                      Home
                    </NavLink>
                    <NavLink to="/company" className={({ isActive }) => barNavCls({ isActive })}>
                      Company
                    </NavLink>
                    <NavLink to="/deals" className={({ isActive }) => barNavCls({ isActive })}>
                      Deals
                    </NavLink>
                    <NavLink to="/events" className={({ isActive }) => barNavCls({ isActive })}>
                      Events
                    </NavLink>
                    <NavLink to="/ira-401k" className={({ isActive }) => barNavCls({ isActive })}>
                      401k / IRA
                    </NavLink>
                    <NavLink to="/contact" className={({ isActive }) => barNavCls({ isActive })}>
                      Contact
                    </NavLink>
                  </div>
                </nav>
              ) : (
                <div className="min-w-0 flex-1" aria-hidden />
              )}
              <nav className="relative z-30 ml-auto flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
                <button
                  type="button"
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.1] bg-white/[0.06] text-[var(--home-text-on-dark)] transition hover:bg-white/[0.1] xl:hidden"
                  aria-expanded={mobileNavOpen}
                  aria-controls="primary-mobile-nav"
                  onClick={() => setMobileNavOpen((o) => !o)}
                >
                  <span className="sr-only">{mobileNavOpen ? 'Close menu' : 'Open menu'}</span>
                  {mobileNavOpen ? <IconClose /> : <IconMenu />}
                </button>
                {!loading && session && profile && !isCompactHeader ? (
                  <UserProfileMenu />
                ) : null}
                {!loading && !session ? (
                  <>
                    <NavLink to="/login" className={({ isActive }) => `${pillNavCls({ isActive })} hidden xl:inline-flex`}>
                      Log in
                    </NavLink>
                    <NavLink
                      to="/getstarted"
                      className="hidden rounded-xl bg-[var(--header-shell-bg)] px-3 py-2 font-display text-[0.65rem] font-bold uppercase tracking-[0.12em] text-white shadow-md shadow-black/20 transition hover:brightness-110 sm:px-4 xl:inline-flex"
                    >
                      Get started
                    </NavLink>
                  </>
                ) : null}
              </nav>
            </div>
          )}
        </div>
        </div>
      </motion.header>

      {!compactPortalHeader && mobileNavOpen ? (
        <div className="xl:hidden">
          <div
            role="presentation"
            className="fixed inset-0 z-[115] bg-[var(--home-zone-void)]/70 backdrop-blur-sm"
            onClick={closeMobileNav}
            aria-hidden
          />
          <nav
            id="primary-mobile-nav"
            className="fixed left-0 top-0 z-[118] flex h-[min(100dvh,100%)] max-h-[100dvh] w-[min(100%,20rem)] flex-col gap-0.5 overflow-y-auto border-r border-white/[0.08] bg-[var(--home-zone-dark)]/98 px-3 pb-10 pt-[calc(5.25rem+env(safe-area-inset-top))] shadow-[12px_0_48px_rgba(0,0,0,0.35)] backdrop-blur-md supports-[backdrop-filter]:bg-[var(--home-zone-dark)]/92"
            aria-label="Primary mobile"
          >
            {!hideMarketingNav ? (
              <>
                <NavLink to="/" className={mobileNavLinkCls} end onClick={closeMobileNav}>
                  Home
                </NavLink>
                <NavLink to="/company" className={mobileNavLinkCls} onClick={closeMobileNav}>
                  Company
                </NavLink>
                <NavLink to="/deals" className={mobileNavLinkCls} onClick={closeMobileNav}>
                  Deals
                </NavLink>
                <NavLink to="/events" className={mobileNavLinkCls} onClick={closeMobileNav}>
                  Events
                </NavLink>
                <NavLink to="/ira-401k" className={mobileNavLinkCls} onClick={closeMobileNav}>
                  401k / IRA
                </NavLink>
                <NavLink to="/contact" className={mobileNavLinkCls} onClick={closeMobileNav}>
                  Contact
                </NavLink>
              </>
            ) : null}

            {session && profile ? (
              <UserProfileMenu forMobileDrawer onNavigate={closeMobileNav} />
            ) : null}

            {!hideMarketingNav && !loading && !session ? (
              <>
                <div className="my-3 border-t border-white/[0.08]" role="presentation" />
                <p className="px-4 pb-1 pt-2 font-mono text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                  Account
                </p>
                <NavLink to="/login" className={mobileNavLinkCls} onClick={closeMobileNav}>
                  Log in
                </NavLink>
                <NavLink to="/getstarted" className={`${mobileNavCtaCls} mx-1 mt-1`} onClick={closeMobileNav}>
                  Get started
                </NavLink>
              </>
            ) : null}
          </nav>
        </div>
      ) : null}

      {/* Stacking: footer sibling must not occlude fixed layers inside Outlet (hero). */}
      <main className="relative z-10 min-w-0 flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            className="min-w-0 w-full"
            initial={reduceMotion ? undefined : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
            transition={pageTransition}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
      <SiteFooter variant={showAdminFooter ? 'admin' : 'marketing'} />
      <OpportunityCallout reduceMotion={Boolean(reduceMotion)} />
      <MarketingCornerPopoverStack reduceMotion={Boolean(reduceMotion)} />
    </div>
    </FloatingCornerProvider>
  );
}
